import { ACTIVE_INVOICE_STATUSES, INVOICE_STATUS } from '../../utils/invoiceSchema';

export const getInvoiceStatusMeta = (status) => {
  switch (status) {
    case INVOICE_STATUS.READY:
      return { label: 'Onaylandı', color: 'success' };
    case INVOICE_STATUS.CANCELLED:
      return { label: 'İptal Edildi', color: 'default', sx: { bgcolor: '#e2e8f0', color: '#64748b', fontWeight: 800 } };
    case INVOICE_STATUS.ERROR:
      return { label: 'Hatalı', color: 'error' };
    case INVOICE_STATUS.SENDING:
      return { label: 'Gönderiliyor', color: 'warning' };
    case INVOICE_STATUS.SENT:
      return { label: 'Gönderildi', color: 'info' };
    case INVOICE_STATUS.APPROVED:
      return { label: 'GİB Onaylı', color: 'success' };
    case INVOICE_STATUS.DRAFT:
    default:
      return { label: 'Taslak', color: 'warning' };
  }
};

/** Fatura EDM'e geçmeden önce Kolaysoft üzerinden kesilmiş (arşivden eşleştirildi). */
export const isKolaysoftIssued = (match) => match?.externalInvoice?.provider === 'kolaysoft';

/** Kayda göre durum etiketi — Kolaysoft'ta kesilenler ayrı etiketle görünür. */
export const getMatchStatusMeta = (match) =>
  isKolaysoftIssued(match)
    ? { label: "Kolaysoft'tan kesildi", color: 'default', sx: { bgcolor: '#dcfce7', color: '#166534', fontWeight: 800 } }
    : getInvoiceStatusMeta(match?.invoiceStatus);

const isActiveInvoiceStatus = (match) => ACTIVE_INVOICE_STATUSES.includes(match?.invoiceStatus);

const resolveLinkStatus = (matches = []) => {
  if (!matches.length) return null;

  const activeMatch = matches.find((row) => isActiveInvoiceStatus(row));
  const cancelledMatches = matches.filter((row) => row.invoiceStatus === INVOICE_STATUS.CANCELLED);

  if (activeMatch) {
    return {
      matchId: activeMatch.id,
      invoiceStatus: activeMatch.invoiceStatus,
      isActive: true,
      isLocked: true,
      hasCancelledHistory: cancelledMatches.length > 0,
      cancelledOnly: false
    };
  }

  if (cancelledMatches.length > 0) {
    const latest = cancelledMatches[cancelledMatches.length - 1];
    return {
      matchId: latest.id,
      invoiceStatus: INVOICE_STATUS.CANCELLED,
      isActive: false,
      isLocked: false,
      hasCancelledHistory: true,
      cancelledOnly: true
    };
  }

  return null;
};

/** Eşleştirme sekmesi için banka ve işlem durum haritaları. */
export const buildInvoiceLinkStatusMaps = (invoiceMatches = []) => {
  const bankBuckets = {};
  const islemBuckets = {};

  for (const match of invoiceMatches) {
    // Çok-hareket eşleşmesinde hepsi "kilitli" görünmeli
    const bankIds = match?.bankMovementIds?.length
      ? match.bankMovementIds.map(String)
      : match?.bankMovementId
        ? [String(match.bankMovementId)]
        : [];
    bankIds.forEach((bankId) => {
      (bankBuckets[bankId] ||= []).push(match);
    });

    const islemKeys = new Set();
    if (match?.islemId) islemKeys.add(match.islemId);
    (match?.islemIds || []).forEach((id) => islemKeys.add(id));
    islemKeys.forEach((id) => {
      (islemBuckets[id] ||= []).push(match);
    });
  }

  const bankMovementStatusMap = {};
  Object.entries(bankBuckets).forEach(([id, rows]) => {
    const status = resolveLinkStatus(rows);
    if (status) bankMovementStatusMap[id] = status;
  });

  const islemStatusMap = {};
  Object.entries(islemBuckets).forEach(([id, rows]) => {
    const status = resolveLinkStatus(rows);
    if (status) islemStatusMap[id] = status;
  });

  return { bankMovementStatusMap, islemStatusMap };
};

/** Eşleştirme listesi chip metni. */
export const getInvoiceMatchListChipMeta = (linkStatus) => {
  if (!linkStatus) return null;

  if (linkStatus.cancelledOnly) {
    return {
      label: 'İptal geçmişi var',
      color: 'default',
      sx: { bgcolor: '#f1f5f9', color: '#64748b', fontWeight: 700, fontSize: 10, height: 20 }
    };
  }

  switch (linkStatus.invoiceStatus) {
    case INVOICE_STATUS.READY:
      return { label: 'Onaylandı', color: 'primary', sx: { fontWeight: 800, fontSize: 10, height: 20 } };
    case INVOICE_STATUS.SENDING:
      return { label: 'Gönderiliyor', color: 'warning', sx: { fontWeight: 800, fontSize: 10, height: 20 } };
    case INVOICE_STATUS.SENT:
      return { label: 'Gönderildi', color: 'info', sx: { fontWeight: 800, fontSize: 10, height: 20 } };
    case INVOICE_STATUS.APPROVED:
      return { label: 'GİB Onaylı', color: 'success', sx: { fontWeight: 800, fontSize: 10, height: 20 } };
    case INVOICE_STATUS.ERROR:
      return { label: 'Hatalı', color: 'error', sx: { fontWeight: 800, fontSize: 10, height: 20 } };
    case INVOICE_STATUS.CANCELLED:
      return {
        label: 'İptal',
        color: 'default',
        sx: { bgcolor: '#e2e8f0', color: '#64748b', fontWeight: 800, fontSize: 10, height: 20 }
      };
    case INVOICE_STATUS.DRAFT:
    default:
      return { label: 'Taslak', color: 'warning', sx: { fontWeight: 800, fontSize: 10, height: 20 } };
  }
};

export const getInvoiceMatchListLabel = (linkStatus) =>
  getInvoiceMatchListChipMeta(linkStatus)?.label || '-';

/** Tablo satırı arka plan rengi. */
export const getInvoiceMatchRowBackground = (linkStatus, { defaultBank = false } = {}) => {
  if (!linkStatus) {
    return defaultBank ? 'rgba(220,252,231,0.45)' : 'inherit';
  }

  if (linkStatus.cancelledOnly) return 'rgba(241,245,249,0.95)';

  switch (linkStatus.invoiceStatus) {
    case INVOICE_STATUS.DRAFT:
      return 'rgba(254,249,195,0.65)';
    case INVOICE_STATUS.READY:
      return 'rgba(219,234,254,0.65)';
    case INVOICE_STATUS.SENDING:
      return 'rgba(254,243,199,0.75)';
    case INVOICE_STATUS.SENT:
      return 'rgba(237,233,254,0.65)';
    case INVOICE_STATUS.APPROVED:
      return 'rgba(220,252,231,0.75)';
    case INVOICE_STATUS.ERROR:
      return 'rgba(254,226,226,0.65)';
    case INVOICE_STATUS.CANCELLED:
      return 'rgba(241,245,249,0.95)';
    default:
      return defaultBank ? 'rgba(220,252,231,0.45)' : 'inherit';
  }
};
